import { type BoardQueueRow, displayedAdministrativeSex } from "../api/clinicalWorkspace";
import type { QueueStatus } from "../types";

export const boardStatusLabels: Record<QueueStatus, string> = {
  waiting: "Waiting",
  rooming: "Rooming",
  ready: "Ready for clinician",
  "with-clinician": "With clinician",
  "wrap-up": "Wrap-up",
};

export function isScheduled(row: BoardQueueRow): boolean {
  return row.appointmentStatus === "scheduled";
}

export function isActiveBoardRow(row: BoardQueueRow): boolean {
  return !["completed", "cancelled", "left"].includes(row.appointmentStatus ?? "");
}

export function boardRoom(row: Pick<BoardQueueRow, "room">): string | undefined {
  const room = row.room?.trim();
  return room && !["lobby", "unassigned"].includes(room.toLowerCase()) ? room : undefined;
}

export function patientDemographics(row: BoardQueueRow): string {
  const age = row.age === undefined ? "Age unknown" : `${row.age}`;
  const sex = displayedAdministrativeSex(row.administrativeSex);
  return sex ? `${age} · ${sex === "male" ? "Male" : "Female"}` : age;
}

function arrivalTime(row: BoardQueueRow): number {
  const timestamp = row.arrivedAt === undefined ? Number.NaN : Date.parse(row.arrivedAt);
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
}

/** Readiness comes from the recorded stage, never task counts or inferred clinical facts. */
export function orderBoardRows(rows: readonly BoardQueueRow[]): BoardQueueRow[] {
  return [...rows].sort((a, b) => {
    const readiness = Number(b.status === "ready") - Number(a.status === "ready");
    const wait = b.waitMinutes - a.waitMinutes;
    const arrival = arrivalTime(a) - arrivalTime(b);
    if (readiness) return readiness;
    if (wait) return wait;
    if (arrival && !Number.isNaN(arrival)) return arrival;
    return a.encounterId.localeCompare(b.encounterId);
  });
}

export function getBoardGroups(rows: readonly BoardQueueRow[]) {
  const active = rows.filter(isActiveBoardRow);
  const inClinic = active.filter((row) => !isScheduled(row));
  return {
    ready: orderBoardRows(inClinic.filter((row) => row.status === "ready")),
    preparing: orderBoardRows(
      inClinic.filter((row) => row.status === "waiting" || row.status === "rooming"),
    ),
    inVisit: orderBoardRows(inClinic.filter((row) => row.status === "with-clinician")),
    wrapUp: orderBoardRows(inClinic.filter((row) => row.status === "wrap-up")),
    scheduled: orderBoardRows(active.filter(isScheduled)),
  };
}

export function getBoardMetrics(rows: readonly BoardQueueRow[]) {
  const inClinic = rows.filter((row) => isActiveBoardRow(row) && !isScheduled(row));
  const waits = inClinic
    .filter((row) => ["waiting", "rooming", "ready"].includes(row.status))
    .map((row) => row.waitMinutes)
    .sort((a, b) => a - b);
  const lowerMiddle = waits[Math.floor((waits.length - 1) / 2)] ?? 0;
  const upperMiddle = waits[Math.floor(waits.length / 2)] ?? 0;
  const medianWait = (lowerMiddle + upperMiddle) / 2;
  const occupiedRooms = inClinic.map(boardRoom).filter((room) => room !== undefined);
  return {
    inClinic: inClinic.length,
    medianWait,
    longestWait: waits.at(-1) ?? 0,
    roomsInUse: new Set(occupiedRooms).size,
  };
}

export function canStartVisit(row: BoardQueueRow): boolean {
  return (
    row.appointmentStatus === "roomed" &&
    boardRoom(row) !== undefined &&
    row.appointmentId !== undefined &&
    row.appointmentVersion !== undefined
  );
}
